import { createSelector, MemoizedSelector } from '@ngrx/store';
import moment from 'moment';
import { selectIsTimesheetPauseCode } from '../../config/selectors/config.selector';
import {
    buildWeeklyWorkdays,
    WeeklyWorkdayModel,
    WORKDAY_KEY_FORMAT,
    workDurationsByDate,
    WorkdayDurations,
} from '../utils/weekly-work-time';
import { selectTimesheetsMerged } from './timesheet-merge.selector';

/**
 * Weekly work time derived from the timesheet collection.
 *
 * The collection keeps every submitted timesheet next to the open one, so the hours worked on
 * the past days of the running week can be read back off it. That makes the separately
 * persisted weekly work time model redundant: no cross-device copy to reconcile, no week
 * rollover to reset, and no backfill for days the app was not opened on — see
 * `utils/weekly-work-time.ts` for the derivation.
 *
 * Read off {@link selectTimesheetsMerged} rather than the raw slice, because the time a technician
 * has recorded on a confirmation still being worked on is not in the slice at all: it lives in the
 * confirmation slice and is merged in at read time. Reading the raw collection would leave the
 * current job out of the panel until its confirmation is submitted, which is precisely the work the
 * in-progress figure exists to show.
 *
 * The week window depends on the current date, which is not part of the store, so the
 * projections below read `moment()`. NgRx memoizes on inputs, so they recompute when the collection,
 * the open confirmations, or the pause-code config change — on a sync, a submit, or a time entry —
 * but not when the clock passes midnight.
 */

/**
 * Committed and in-progress working minutes per calendar day, keyed by {@link WORKDAY_KEY_FORMAT},
 * over the whole collection. Not restricted to the running week — the week window is applied by
 * {@link selectWeeklyWorkdays}.
 */
export const selectWorkDurationsByDate: MemoizedSelector<object, Record<string, WorkdayDurations>> = createSelector(
    selectTimesheetsMerged,
    selectIsTimesheetPauseCode,
    (timesheets, isPauseCode) => workDurationsByDate(timesheets, isPauseCode),
);

/** The days of the running ISO week up to today, Monday first. Replaces `getWeeklyWorkTimeList`. */
export const selectWeeklyWorkdays: MemoizedSelector<object, WeeklyWorkdayModel[]> = createSelector(
    selectWorkDurationsByDate,
    (durationsByDate) => buildWeeklyWorkdays(durationsByDate, moment()),
);

/** Committed minutes worked so far in the running ISO week — confirmed work, excluding anything still in progress. */
export const selectTotalWeekCommittedInMinutes: MemoizedSelector<object, number> = createSelector(
    selectWeeklyWorkdays,
    (workdays) => workdays.reduce((total, workday) => total + workday.committedInMinutes, 0),
);

/** In-progress minutes recorded so far in the running ISO week — not yet confirmed. */
export const selectTotalWeekInProgressInMinutes: MemoizedSelector<object, number> = createSelector(
    selectWeeklyWorkdays,
    (workdays) => workdays.reduce((total, workday) => total + workday.inProgressInMinutes, 0),
);

/**
 * Minutes worked so far in the running ISO week, committed and in-progress summed together.
 * Backs `getTotalWeekDuration`. Deliberately keeps summing both, so the "weekly hours exceeded"
 * check behaves exactly as it did before the split.
 */
export const selectTotalWeekDurationInMinutes: MemoizedSelector<object, number> = createSelector(
    selectTotalWeekCommittedInMinutes,
    selectTotalWeekInProgressInMinutes,
    (committed, inProgress) => committed + inProgress,
);

/**
 * Minutes worked today, committed and in-progress summed together. Backs
 * `getCurrentDayTotalDuration`.
 */
export const selectCurrentDayDurationInMinutes: MemoizedSelector<object, number> = createSelector(
    selectWorkDurationsByDate,
    (durationsByDate) => {
        const today = durationsByDate[moment().format(WORKDAY_KEY_FORMAT)];
        return (today?.committedInMinutes ?? 0) + (today?.inProgressInMinutes ?? 0);
    },
);
